import classNames from 'classnames';
import { map } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import Carousel from 'components/Carousel';
import { Container } from 'components/Grid';
import { LargeUp } from 'components/MediaQuery';
import { small, medium, mediumUp, largeUp, breakpoints } from 'constants/mediaQueries';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';

import NavigationArrow from './NavigationArrow';

const SLIDES_PER_VIEWPORT = {
  SMALL: 1,
  MEDIUM: 2,
  LARGE: 3,
};

const SLIDE_SPACING_SMALL = Theme.get('spacing') * 2;
const SLIDE_SPACING_LARGE = Theme.get('spacing') * 4;

// determines how much of next slide to let peek through -
// 24px for tablet, 48px for desktop
const CAROUSEL_PADDING_MEDIUM = SLIDE_SPACING_SMALL;
const CAROUSEL_PADDING_LARGE = SLIDE_SPACING_LARGE * 2;

// used to keep card's box shadow visible
const BOTTOM_PADDING_SMALL = Theme.get('spacing') * 12;
const BOTTOM_PADDING_MEDIUM = Theme.get('spacing') * 13;
const BOTTOM_PADDING_LARGE = Theme.get('spacing') * 20;

class HomepageV2Carousel extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      activeSlideIndex: 0,
    };
  }

  handleSlideSelect = index => {
    const { activeSlideIndex } = this.state;
    if (index === activeSlideIndex) return;

    this.setState({ activeSlideIndex: index });
  };

  render() {
    const { children, slidesPerViewPort } = this.props;
    const { activeSlideIndex } = this.state;
    const { SMALL, MEDIUM, LARGE } = slidesPerViewPort;

    if (!SMALL || !MEDIUM || !LARGE) {
      return null;
    }

    const SLIDES_PER_PAGE = {
      [breakpoints.small.min]: SMALL,
      [breakpoints.medium.min]: MEDIUM,
      [breakpoints.large.min]: LARGE,
    };

    return (
      <div className="HomepageV2Carousel">
        <Container>
          <div className="HomepageV2Carousel__carousel-wrapper">
            <Carousel
              key="base-carousel-homepage-v2"
              currentSlide={activeSlideIndex}
              options={{
                perPage: SLIDES_PER_PAGE,
                loop: false,
              }}
              onChange={currentSlide => this.handleSlideSelect(currentSlide)}
            >
              {map(children, (slide, index) => (
                <div key={index} className="HomepageV2Carousel__slide-container">
                  {slide}
                </div>
              ))}
            </Carousel>
          </div>
          <LargeUp>
            <div
              className={classNames('HomepageV2Carousel__left-arrow', {
                'HomepageV2Carousel__left-arrow--active': activeSlideIndex > 0,
              })}
            >
              <NavigationArrow
                direction="left"
                aria-label={I18n.localize('carousel.viewPreviousSlide')}
                onClick={() => this.handleSlideSelect(activeSlideIndex - 1)}
              />
            </div>
            <div
              className={classNames('HomepageV2Carousel__right-arrow', {
                'HomepageV2Carousel__right-arrow--active-small':
                  activeSlideIndex + SMALL < children.length,
                'HomepageV2Carousel__right-arrow--active-medium':
                  activeSlideIndex + MEDIUM < children.length,
                'HomepageV2Carousel__right-arrow--active-large':
                  activeSlideIndex + LARGE < children.length,
              })}
            >
              <NavigationArrow
                direction="right"
                aria-label={I18n.localize('carousel.viewNextSlide')}
                onClick={() => this.handleSlideSelect(activeSlideIndex + 1)}
              />
            </div>
          </LargeUp>
        </Container>
        <style jsx>{`
          .HomepageV2Carousel {
            position: relative;
            height: 100%;
            width: 100%;
            overflow: hidden;
            padding-top: ${Theme.get('spacing') * 2}px;
            padding-bottom: ${BOTTOM_PADDING_SMALL}px;
          }

          @media ${mediumUp} {
            .HomepageV2Carousel {
              padding-bottom: ${BOTTOM_PADDING_MEDIUM}px;
            }
          }

          @media ${largeUp} {
            .HomepageV2Carousel {
              padding-bottom: ${BOTTOM_PADDING_LARGE}px;
            }
          }

          @media ${medium} {
            .HomepageV2Carousel__carousel-wrapper {
              padding: 0 ${CAROUSEL_PADDING_MEDIUM}px;
            }
          }

          @media ${largeUp} {
            .HomepageV2Carousel__carousel-wrapper {
              padding: 0 ${CAROUSEL_PADDING_LARGE}px;
            }
          }

          .HomepageV2Carousel__slide-container {
            margin: 0 ${SLIDE_SPACING_SMALL / 2}px;
            display: flex;
            height: 100%;
          }

          @media ${largeUp} {
            .HomepageV2Carousel__slide-container {
              margin: 0 ${SLIDE_SPACING_LARGE / 2}px;
            }
          }

          .HomepageV2Carousel__left-arrow,
          .HomepageV2Carousel__right-arrow {
            position: absolute;
            top: calc(50% - ${BOTTOM_PADDING_SMALL / 2}px);
            transform: translateY(-50%);
            opacity: 0;
            visibility: hidden;
            transition: opacity ${Theme.get('transition-duration') / 2}ms ease;
          }

          @media ${mediumUp} {
            .HomepageV2Carousel__left-arrow,
            .HomepageV2Carousel__right-arrow {
              top: calc(50% - ${BOTTOM_PADDING_MEDIUM / 2}px);
            }
          }

          @media ${largeUp} {
            .HomepageV2Carousel__left-arrow,
            .HomepageV2Carousel__right-arrow {
              top: calc(50% - ${BOTTOM_PADDING_LARGE / 2}px);
            }
          }

          .HomepageV2Carousel__left-arrow--active {
            opacity: 1;
            visibility: visible;
          }

          @media ${small} {
            .HomepageV2Carousel__right-arrow--active-small {
              opacity: 1;
              visibility: visible;
            }
          }

          @media ${medium} {
            .HomepageV2Carousel__right-arrow--active-medium {
              opacity: 1;
              visibility: visible;
            }
          }

          @media ${largeUp} {
            .HomepageV2Carousel__right-arrow--active-large {
              opacity: 1;
              visibility: visible;
            }
          }

          .HomepageV2Carousel__right-arrow {
            right: ${Theme.get('spacing') * 8}px;
          }

          .HomepageV2Carousel__left-arrow {
            left: ${Theme.get('spacing') * 8}px;
          }
        `}</style>
        <style jsx global>
          {`
            .HomepageV2Carousel .Carousel {
              overflow: visible !important;
              height: 100%;
              margin: 0 -${SLIDE_SPACING_SMALL / 2}px;
            }

            // create new block formatting context
            .HomepageV2Carousel .Carousel::after {
              content: '';
              display: table;
            }

            @media ${largeUp} {
              .HomepageV2Carousel .Carousel {
                margin: 0 -${SLIDE_SPACING_LARGE / 2}px;
              }
            }

            .HomepageV2Carousel .Carousel > div {
              display: flex;
            }

            .HomepageV2Carousel .Carousel > div > div {
              flex: 1;
            }
          `}
        </style>
      </div>
    );
  }
}

HomepageV2Carousel.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  slidesPerViewPort: PropTypes.object,
};

HomepageV2Carousel.defaultProps = {
  slidesPerViewPort: SLIDES_PER_VIEWPORT,
  children: null,
};

export default HomepageV2Carousel;
